import type { AxiosRequestConfig } from "axios";
import { useDispatch, useSelector } from "react-redux";
import type { IUser } from "../../types/reducers/authReducer";
import { hasPermission, canAccessAny as legacyCanAccessAny, getWorkspaceRole, isVolunteerOnly } from "../../utils/permissions";
import { showsVolunteeringFor } from "../../utils/showsVolunteering";
import { authTriggerActions } from "../../backend/store/reducers/authReducer";
import { billingTriggerActions } from "../../backend/store/actions/creators/billingActions";
import { useImportProgress } from "../../contexts/ImportProgressContext";
import { useImpersonation } from "../../components/base/Impersonation";
import { useAddNotification } from "../../hooks/useAddNotification";
import { legacyAxios } from "./apiClient";
import { reportChangeError } from "../data/queryClient";
import { viewOnlyOf } from "./billing";
export { default as LegacyContactUsModal } from "../../components/base/ContactUsModal/ContactUsModal";
export { default as LegacyTutorialsModal } from "../../components/base/TutorialsModal/TutorialsModal";
export { LegalDocumentsDialog as LegacyLegalDocumentsDialog } from "../../components/base/LegalDocuments/LegalDocumentsDialog";
export { default as LegacyExportDataModal } from "../../components/Onboarding/ExportDataModal";
export { SetPasswordModal as LegacySetPasswordModal } from "../../components/base/SetPasswordModal";

const useUser = () => useSelector((s: any) => (s.authStore?.currentUser ?? null) as IUser | null);

/** Mirrors PrivateRoute's lock rule (billing on, not super admin, no plan, no running trial, not pre-trial) with SideBar's pre-trial definition (excludes the staff workspace). */
export const useBillingState = () => {
  const user = useUser();
  const b = useSelector((s: any) => s.billingStore ?? {});
  const hasPlan = !!b.hasActiveSubscription;
  const trialActive = !!b.isTrialActive;
  const preTrial = b.trialEndsAt === null && !hasPlan && !b.isSuperAdminWorkspace;
  const isSuper = user?.role === "super_admin";
  const locked = !!b.billingEnabled && !isSuper && !hasPlan && !trialActive && !preTrial;
  const viewOnly = viewOnlyOf(b);
  return { locked, preTrial, trialActive, hasPlan, contactCount: Number(b.contactCount ?? 0), viewOnly };
};

export const useAccess = () => {
  const user = useUser();
  return {
    has: (p: string | string[]) => hasPermission(p, user),
    canAccessAny: (p: string, scope: "routes" | "households") => legacyCanAccessAny(user, p, scope),
    showsVolunteering: !!user && showsVolunteeringFor(user as any),
    workspaceRole: getWorkspaceRole(user) ?? null,
    isVolunteerOnly: isVolunteerOnly(user),
  };
};

/** Only people with billing rights may read the subscription and usage; the others used to get two 403s on every page. */
export const useRefreshBilling = () => {
  const dispatch = useDispatch();
  const user = useUser();
  return () => {
    if (!hasPermission("read_billing", user)) return;
    dispatch(billingTriggerActions.getSubscription()); dispatch(billingTriggerActions.getUsage());
  };
};

/** Report drafts are removed by today's logout saga once the sign-out went through (plan 2D F5). */
export const useSignOut = () => {
  const dispatch = useDispatch();
  return () => dispatch(authTriggerActions.logoutUserTriggerAC());
};

/**
 * Same URL and full reload as today's SelectWorkspace, but through the shared client
 * (Bearer token + the usual 401/402 handling), unlike today's cookie-only axios call.
 */
export const useSwitchWorkspace = () => async (workspaceId: number) => {
  try {
    // nextLayer: this call reports its own refusal, so the old pages' interceptor stays quiet (one toast).
    await legacyAxios.post("/api/auth/switch-workspace", { workspaceId }, { nextLayer: true } as AxiosRequestConfig);
  } catch (e) {
    // Staff viewing as someone may not switch their account: the shell says so (a toast); nothing else changes.
    reportChangeError(e);
    return;
  }
  window.location.href = "/";
};

export const useImportRunning = (): boolean => {
  try {
    return useImportProgress().isImportInProgress;
  } catch {
    return false; // outside ImportProgressProvider (the context read precedes the throw, so hook order is stable)
  }
};

/** One "viewing as" rule for the whole app (2.6): parked admin token OR the user's flag. */
export const useViewingAs = () => {
  const v = useImpersonation();
  return { active: !!v.active, name: v.name ?? "", account: v.account ?? "", exit: v.exit };
};

/** Today's live pop-up, so new notifications keep their current look until 1B-2. */
export { useAddNotification as useAddLegacyToast };
